import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from 'antd'
import { PiChatCircleDots } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import VotitoSurvey from '.'
import { useVotitoSurvey } from './useVotitoSurvey'

const AUTO_TRIGGER_DELAY_MS = 10_000
const AUTO_TRIGGER_STORAGE_KEY = 'vt-survey-auto-triggered'

interface VotitoSurveyConnectedProps {
  surveyId: string
  enabled?: boolean
  autoTrigger?: boolean
  showButton?: boolean
  // Fires once the survey can no longer pop up on its own (already answered, failed
  // to load, or just answered/dismissed), so callers can chain other modals after it.
  onResolve?: () => void
  // Tracks the survey modal opening/closing, so callers can mute competing UI
  // (tutorial popovers) while the survey is on screen.
  onVisibleChange?: (visible: boolean) => void
}

const wasAutoTriggered = (surveyId: string): boolean => {
  try {
    const ids: string[] = JSON.parse(localStorage.getItem(AUTO_TRIGGER_STORAGE_KEY) || '[]')
    return ids.includes(surveyId)
  } catch {
    return false
  }
}

const markAutoTriggered = (surveyId: string): void => {
  try {
    const ids: string[] = JSON.parse(localStorage.getItem(AUTO_TRIGGER_STORAGE_KEY) || '[]')
    if (!ids.includes(surveyId)) {
      ids.push(surveyId)
      localStorage.setItem(AUTO_TRIGGER_STORAGE_KEY, JSON.stringify(ids))
    }
  } catch {
    // ignore
  }
}

const VotitoSurveyConnected: React.FC<VotitoSurveyConnectedProps> = ({ surveyId, enabled = true, autoTrigger = true, showButton = true, onResolve, onVisibleChange }) => {
  const { formatMessage } = useIntl()
  const [visible, setVisible] = useState(false)
  const { survey, isCompleted, hasLoaded, submitResponses, dismiss } = useVotitoSurvey({ surveyId, enabled })
  const autoTriggeredRef = useRef(false)
  const resolvedRef = useRef(false)
  // Read once: markAutoTriggered() writes this key when the modal opens, and
  // re-reading it later would resolve while the survey is still on screen.
  const autoTriggeredBeforeMountRef = useRef(wasAutoTriggered(surveyId))

  const resolve = useCallback(() => {
    if (resolvedRef.current) return
    resolvedRef.current = true
    onResolve?.()
  }, [onResolve])

  const surveyLoaded = !!survey

  useEffect(() => {
    onVisibleChange?.(visible)
  }, [visible, onVisibleChange])

  useEffect(() => {
    // These are known up front, so don't make callers wait on a network round trip
    // for a survey that can never pop up on its own.
    if (!enabled || !autoTrigger || autoTriggeredBeforeMountRef.current) {
      resolve()
      return
    }
    if (!hasLoaded) return
    if (isCompleted || !surveyLoaded) {
      resolve()
    }
  }, [enabled, hasLoaded, isCompleted, surveyLoaded, autoTrigger, resolve])

  useEffect(() => {
    if (!autoTrigger) return
    if (!surveyLoaded || isCompleted || autoTriggeredRef.current || wasAutoTriggered(surveyId)) return

    const timer = setTimeout(() => {
      autoTriggeredRef.current = true
      markAutoTriggered(surveyId)
      setVisible(true)
    }, AUTO_TRIGGER_DELAY_MS)

    return () => clearTimeout(timer)
  }, [surveyLoaded, isCompleted, surveyId, autoTrigger])

  const handleComplete = async (responses: any) => {
    // Keep the modal open on a failed submit so the answers aren't lost without the user noticing.
    if (!(await submitResponses(responses))) return
    setVisible(false)
    resolve()
  }

  const handleDismiss = async () => {
    await dismiss()
    markAutoTriggered(surveyId)
    setVisible(false)
    resolve()
  }

  // Don't render anything if survey is completed or not loaded
  if (isCompleted || !survey) return null

  return (
    <>
      {showButton && (
        <Button icon={<PiChatCircleDots />} onClick={() => setVisible(true)}>
          {formatMessage({ id: 'survey.giveFeedback' })}
        </Button>
      )}
      <VotitoSurvey survey={survey} visible={visible} onComplete={handleComplete} onDismiss={handleDismiss} />
    </>
  )
}

export default VotitoSurveyConnected
